import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, Plus, Check } from '@phosphor-icons/react';
import { Card, CardContent } from '../ui/Card';
import { IntegrationLogo } from './IntegrationLogo';
import { IntegrationStatusBadge } from './IntegrationStatusBadge';
import { searchIntegrationCatalog } from '../../data/integrationsMock';
import { cn } from '../../lib/utils';

export function IntegrationSearch({
  installedProviders = [],
  busyProvider = null,
  onAdd,
  className,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const results = useMemo(() => searchIntegrationCatalog(query), [query]);
  const installed = new Set(installedProviders);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  return (
    <Card className={cn('min-w-0 max-w-2xl', className)}>
      <CardContent className="space-y-3 px-4 py-4">
        <div>
          <h2 className="font-display text-sm font-bold text-[#201C35]">Browse integrations</h2>
          <p className="mt-0.5 text-xs text-slate-600">
            Search platforms like Slack’s app directory, then add them to your workspace.
          </p>
        </div>

        <div ref={rootRef} className="relative">
          <label htmlFor="integration-search" className="sr-only">
            Search integrations
          </label>
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            id="integration-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search e-conomic, MobilePay, HubSpot…"
            className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-[var(--brand-primary,#1A428A)] focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15"
            autoComplete="off"
          />

          {open ? (
            <div className="absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.14)]">
              {results.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-slate-500">
                  No integrations match “{query}”.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 py-1">
                  {results.map((item) => {
                    const isInstalled = installed.has(item.provider);
                    const canAdd = item.available && !isInstalled;
                    return (
                      <li
                        key={item.id}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50"
                      >
                        <IntegrationLogo
                          logoKey={item.logoKey}
                          name={item.name}
                          src={item.logoUrl}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-display truncate text-sm font-bold text-[#201C35]">
                              {item.name}
                            </p>
                            {!item.available ? (
                              <IntegrationStatusBadge status="coming_soon" className="ml-1" />
                            ) : null}
                          </div>
                          <p className="mt-0.5 truncate text-xs text-slate-500">{item.outcome}</p>
                        </div>
                        {isInstalled ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                            <Check size={12} weight="bold" aria-hidden="true" />
                            Added
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={!canAdd || busyProvider === item.provider}
                            onClick={() => {
                              onAdd?.(item);
                              setOpen(false);
                              setQuery('');
                            }}
                            className={cn(
                              'inline-flex h-8 items-center justify-center gap-1 rounded-full px-3 text-xs font-semibold transition duration-150',
                              'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/20',
                              'disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100',
                              canAdd
                                ? 'bg-[var(--brand-primary,#1A428A)] text-white shadow-md hover:bg-[#163872]'
                                : 'bg-slate-100 text-slate-500'
                            )}
                          >
                            <Plus size={12} weight="bold" aria-hidden="true" />
                            {item.available ? 'Add' : 'Soon'}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
